import React, { useMemo, useState } from 'react';
import StbButton from '../security-toolbox-ui/StbButton';
import { useLabAgents, useRevokeLabAgent } from '../../hooks/lab-hooks';
import { LabAgent, LabAgentStatus, labAgentSentiment } from '../../support/labService';
import LabAgentEnrollModal from './LabAgentEnrollModal';
import LabAgentRevokeModal from './LabAgentRevokeModal';
import {
  LabCard,
  LabDate,
  LabJsonView,
  LabPanelState,
  LabStatusTag,
  labDuration,
} from './LabShared';

export type LabAgentsPanelProps = {
  /** Narrow the fleet to one session, e.g. from a run or an assist case. */
  sessionKey?: string;
  /** Enrolling and revoking are steward actions; viewing the fleet is not. */
  canManage?: boolean;
};

const STATUS_ORDER: LabAgentStatus[] = ['offline', 'degraded', 'pending', 'online', 'revoked'];

/**
 * The agent fleet.
 *
 * One agent per lab, living on the pod's utility VM. `status` is the backend's reading, not
 * ours - it counts missed polls against the agent's own interval, so a fleet of agents on
 * different intervals is still comparable here. Sorted worst-first: during an event the
 * useful question is which pods have gone quiet, not which are fine.
 *
 * Revoking is not deleting. A revoked agent's token stops being accepted, and the row stays
 * so there is a record of what was enrolled and by whom.
 */
const LabAgentsPanel: React.FC<LabAgentsPanelProps> = ({ sessionKey, canManage = false }) => {
  const [enrollOpen, setEnrollOpen] = useState(false);
  const [revoking, setRevoking] = useState<LabAgent | null>(null);
  const [expanded, setExpanded] = useState<number | null>(null);

  const { data: agents, isLoading, error, refetch } = useLabAgents();
  const revoke = useRevokeLabAgent();

  const rows = useMemo(() => {
    const visible = sessionKey
      ? (agents || []).filter((agent) => agent.session_key === sessionKey)
      : agents || [];

    return [...visible].sort((left, right) => {
      const byStatus = STATUS_ORDER.indexOf(left.status) - STATUS_ORDER.indexOf(right.status);
      if (byStatus !== 0) return byStatus;

      return (left.name || '').localeCompare(right.name || '');
    });
  }, [agents, sessionKey]);

  const offline = rows.filter((agent) => agent.status === 'offline' && agent.enabled).length;

  return (
    <>
      <LabCard
        title="Lab agents"
        subtitle={
          sessionKey
            ? `Agents reporting from ${sessionKey}.`
            : 'One per pod. Status comes from missed polls, counted against each agent’s own interval.'
        }
        actions={
          <>
            <StbButton role="default" size="small" onClick={async () => refetch()}>
              Refresh
            </StbButton>
            {canManage && (
              <StbButton role="add" size="small" onClick={async () => setEnrollOpen(true)}>
                Enroll an agent
              </StbButton>
            )}
          </>
        }
      >
        {offline > 0 && (
          <p className="m-0 mb-3 text-sm text-red-700">
            {`${offline} agent${offline === 1 ? ' has' : 's have'} stopped polling. Those pods cannot run steps or report health.`}
          </p>
        )}

        <LabPanelState
          loading={isLoading}
          error={error}
          empty={!rows.length}
          emptyMessage={
            sessionKey
              ? 'No agent has enrolled from this session yet.'
              : 'No agents enrolled.'
          }
          loadingLabel="Loading agents..."
        >
          <div className="overflow-x-auto">
            <table className="w-full text-left text-sm">
              <thead>
                <tr className="border-b border-gray-200 text-xs uppercase tracking-wide text-gray-500">
                  <th className="px-3 py-2 font-semibold">Agent</th>
                  <th className="px-3 py-2 font-semibold">Status</th>
                  <th className="px-3 py-2 font-semibold">Session</th>
                  <th className="px-3 py-2 font-semibold">Last seen</th>
                  <th className="px-3 py-2 font-semibold">Polling</th>
                  <th className="px-3 py-2 font-semibold text-right">Actions</th>
                </tr>
              </thead>

              <tbody>
                {rows.map((agent) => (
                  <React.Fragment key={agent.id}>
                    <tr className="border-b border-gray-100 align-top">
                      <td className="px-3 py-2">
                        <div className="font-medium">{agent.name || agent.agent_key}</div>
                        <div className="font-mono text-xs text-gray-500">{agent.agent_key}</div>
                        {agent.version && (
                          <div className="text-xs text-gray-500">{`v${agent.version}`}</div>
                        )}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <LabStatusTag
                          label={agent.status}
                          sentiment={labAgentSentiment(agent.status)}
                        />
                        {!agent.enabled && (
                          <div className="mt-1">
                            <LabStatusTag label="disabled" sentiment="warning" />
                          </div>
                        )}
                        {agent.missed_polls > 0 && agent.status !== 'revoked' && (
                          <div className="text-xs text-amber-700">
                            {`${agent.missed_polls} missed poll${agent.missed_polls === 1 ? '' : 's'}`}
                          </div>
                        )}
                        {agent.last_error && (
                          <div className="text-xs text-red-700">{agent.last_error}</div>
                        )}
                      </td>

                      <td className="px-3 py-2">
                        <div className="text-xs text-gray-600">
                          {agent.session_key || 'Not bound to a session'}
                        </div>
                        <div className="text-xs text-gray-500">{agent.transport}</div>
                        {agent.scenario_slug && (
                          <div className="text-xs text-gray-500">{agent.scenario_slug}</div>
                        )}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <LabDate value={agent.last_seen_at} fallback="Never" />
                        {agent.last_round_trip_ms !== null &&
                          agent.last_round_trip_ms !== undefined && (
                            <div className="text-xs text-gray-500">
                              {`${agent.last_round_trip_ms} ms round trip`}
                            </div>
                          )}
                      </td>

                      <td className="px-3 py-2 whitespace-nowrap">
                        <div className="text-xs text-gray-600">
                          {`every ${labDuration(agent.poll_interval_seconds)}`}
                        </div>
                        <div className="text-xs text-gray-500">
                          {`${agent.poll_count} poll${agent.poll_count === 1 ? '' : 's'}`}
                        </div>
                      </td>

                      <td className="px-3 py-2">
                        <div className="flex items-center justify-end gap-2">
                          <StbButton
                            role="default"
                            size="xsmall"
                            onClick={async () =>
                              setExpanded(expanded === agent.id ? null : agent.id)
                            }
                          >
                            {expanded === agent.id ? 'Hide' : 'Details'}
                          </StbButton>
                          {canManage && agent.status !== 'revoked' && (
                            <StbButton
                              role="delete"
                              size="xsmall"
                              onClick={async () => setRevoking(agent)}
                            >
                              Revoke
                            </StbButton>
                          )}
                        </div>
                      </td>
                    </tr>

                    {expanded === agent.id && (
                      <tr className="border-b border-gray-100 bg-gray-50">
                        <td className="px-3 py-3" colSpan={6}>
                          <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
                            <div>
                              <div className="text-xs uppercase text-gray-500">Enrolled by</div>
                              <div className="text-sm">{agent.enrolled_by_email || '—'}</div>

                              <div className="mt-2 text-xs uppercase text-gray-500">Lease</div>
                              <div className="text-sm">{labDuration(agent.lease_seconds)}</div>

                              <div className="mt-2 text-xs uppercase text-gray-500">
                                Capabilities
                              </div>
                              <div className="text-sm">
                                {(agent.capabilities || []).join(', ') || 'None reported'}
                              </div>

                              {agent.description && (
                                <>
                                  <div className="mt-2 text-xs uppercase text-gray-500">
                                    Description
                                  </div>
                                  <div className="text-sm">{agent.description}</div>
                                </>
                              )}
                            </div>

                            <div>
                              <div className="text-xs uppercase text-gray-500">
                                Telemetry
                                {agent.telemetry_at && (
                                  <span className="ml-1 normal-case text-gray-400">
                                    (<LabDate value={agent.telemetry_at} />)
                                  </span>
                                )}
                              </div>
                              <LabJsonView value={agent.telemetry} />
                            </div>
                          </div>
                        </td>
                      </tr>
                    )}
                  </React.Fragment>
                ))}
              </tbody>
            </table>
          </div>
        </LabPanelState>
      </LabCard>

      <LabAgentEnrollModal open={enrollOpen} onClose={() => setEnrollOpen(false)} />

      <LabAgentRevokeModal
        agent={revoking}
        pending={revoke.isPending}
        onClose={() => setRevoking(null)}
        onConfirm={async (reason) => {
          if (!revoking) return;

          await revoke.mutateAsync({ agentKey: revoking.agent_key, reason });
          setRevoking(null);
        }}
      />
    </>
  );
};

export default LabAgentsPanel;
